/*
 * Factorisation des fonctions communes entre MapDrawView et MapDrawWithClusterView.
 * Ce module évite la duplication (locale Leaflet Draw, construction des groupes, bounds).
 */
import L, { FeatureGroup, LatLngBounds } from 'leaflet'

/** Applique la locale Leaflet Draw à partir d'une fonction de traduction. */
export function applyLeafletLocale(t: (key: string) => string) {
  // @ts-ignore
  L.drawLocal = {
    draw: {
      toolbar: {
        actions: { title: t('mapDraw.toolbar.annuler-title'), text: t('annuler') },
        finish: { title: t('mapDraw.toolbar.finir-title'), text: t('mapDraw.toolbar.finir-text') },
        undo: { title: t('mapDraw.toolbar.supprimer-title'), text: t('mapDraw.toolbar.supprimer-text') },
        buttons: {
          polyline: t('mapDraw.toolbar.polyline'),
          rectangle: t('mapDraw.toolbar.rectangle'),
          circle: t('mapDraw.toolbar.circle'),
          circlemarker: t('mapDraw.toolbar.circlemarker'),
          polygon: t('mapDraw.toolbar.polygon'),
          marker: t('mapDraw.toolbar.marker'),
        },
      },
      handlers: {
        circle: { tooltip: { start: 'Click and drag to draw circle.' }, radius: 'Radius' },
        circlemarker: { tooltip: { start: 'Click map to place circle marker.' } },
        polyline: {
          error: '<strong>Error:</strong> shape edges cannot cross!',
          tooltip: {
            start: 'Click to start drawing line.',
            cont: 'Click to continue drawing line.',
            end: 'Click last point to finish line.',
          },
        },
        rectangle: { tooltip: { start: 'Click and drag to draw rectangle.' } },
        simpleshape: { tooltip: { end: 'Release mouse to finish drawing.' } },
        marker: { tooltip: { start: t('mapDraw.handlers.marker') } },
        polygon: {
          tooltip: {
            start: t('mapDraw.handlers.polygon-start'),
            cont: t('mapDraw.handlers.polygon-cont'),
            end: t('mapDraw.handlers.polygon-end'),
          },
        },
      },
    },
    edit: {
      toolbar: {
        actions: {
          save: { title: t('mapDraw.edit-toolbar.save-title'), text: t('mapDraw.edit-toolbar.save-text') },
          cancel: { title: t('mapDraw.edit-toolbar.cancel-title'), text: t('annuler') },
          clearAll: { title: t('mapDraw.edit-toolbar.clearAll-title'), text: t('mapDraw.edit-toolbar.clearAll-text') },
        },
        buttons: {
          edit: t('mapDraw.edit-toolbar.edit'),
            editDisabled: t('mapDraw.edit-toolbar.editDisabled'),
            remove: t('mapDraw.edit-toolbar.remove'),
            removeDisabled: t('mapDraw.edit-toolbar.removeDisabled'),
        },
      },
      handlers: {
        edit: { tooltip: { text: t('mapDraw.edit-handlers.edit-tooltip-text'), subtext: t('mapDraw.edit-handlers.edit-tooltip-subtext') } },
        remove: { tooltip: { text: t('mapDraw.edit-handlers.remove-tooltip-text') } },
      },
    },
  }
}

/** Calcule un LatLngBounds à partir d'un GeoJSON (Polygon / MultiPolygon) si valide. */
export function boundsFromGeoJSON(geo: any): LatLngBounds | null {
  try {
    if (!geo?.geometry?.coordinates) return null
    const bounds = new LatLngBounds([])
    geo.geometry.coordinates.forEach((geoCoord: any) => {
      for (let i = 0; i < geoCoord.length; i++) {
        const pt = geoCoord[i]
        if (Array.isArray(pt) && pt.length >= 2) bounds.extend([pt[1], pt[0]])
      }
    })
    return bounds.isValid() ? bounds : null
  } catch {
    return null
  }
}

/** Construit un FeatureGroup à partir d'un GeoJSON sérialisé ou retourne un groupe vide. */
export function buildDrawnItems(serialized: string | undefined, renderer?: any): FeatureGroup {
  if (!serialized) return new L.FeatureGroup([], renderer ? { renderer } : undefined)
  try {
    const parsed = JSON.parse(serialized)
    // @ts-ignore
    return L.geoJSON(parsed, renderer ? { renderer } : undefined) as unknown as FeatureGroup
  } catch {
    return new L.FeatureGroup([], renderer ? { renderer } : undefined)
  }
}

/** Met à jour le JSON de zone depuis un layer dessiné. */
export function layerToGeoJSONString(layer: any): string | null {
  try {
    return JSON.stringify(layer.toGeoJSON())
  } catch {
    return null
  }
}

/**
 * Détecte si les coordonnées sont en France métropolitaine.
 * Métropole : lat entre 41° et 51°N, lng entre -5° et 10°E
 * Utilisé pour adapter le maxNativeZoom des tuiles satellite selon la région.
 */
export function isMetropolitanFrance(lat: number | null | undefined, lng: number | null | undefined): boolean {
  if (!lat || !lng || lat === 0 || lng === 0) return false
  return lat >= 41 && lat <= 51 && lng >= -5 && lng <= 10
}
